import { MapFormLayout } from 'components/MapFormLayout';
import { Alert } from 'features/alerts/alerts.types';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import {
  Stack,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  FormControl,
  Typography,
} from '@mui/material';
import { useMapState } from 'state/useMapState';
import { AnyListItemGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import {
  ActiveFloodingsLayer,
  InactiveFloodingsLayer,
} from 'features/mapping/components/layers/FloodingsLayer';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { LocationsLayer } from 'features/mapping/components/layers/LocationsLayer';
import { HazardsLayer } from 'features/mapping/components/layers/HazardsLayer';
import { SingleWarningsAndAlertsLayer } from 'features/mapping/components/layers/NWSWatchesAndWarningsLayer';
import { useTranslation } from 'react-i18next';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { isWildfireGeoEvent } from 'features/geoEvents/geoEvent.utils';
import { useMutation } from '@tanstack/react-query';
import { API } from 'api';
import { enqueueSnackbar } from 'notistack';
import { isEqual } from 'es-toolkit';
import { useHistory } from 'react-router-dom';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';

type EditAlertFormProps = {
  alert: Alert;
};

type FormValues = {
  isVisible: boolean;
  geoEvents: AnyListItemGeoEvent[];
};

type SubmitData = {
  isVisible: boolean;
  geoEventIds: number[];
};

const getStatusOptions = (
  isWea: boolean,
): { transKey: string; value: string }[] =>
  isWea
    ? [
        { transKey: 'editAlert.inputs.status.options.approve', value: 'true' },
        { transKey: 'editAlert.inputs.status.options.hidden', value: 'false' },
      ]
    : [
        { transKey: 'editAlert.inputs.status.options.visible', value: 'true' },
        { transKey: 'editAlert.inputs.status.options.hidden', value: 'false' },
      ];

const patchAlert = async (alertId: number, data: SubmitData): Promise<void> => {
  await API.patch(`warnings_and_alerts/${alertId}/`, data);
};

export const EditAlertForm = (props: EditAlertFormProps): JSX.Element => {
  const { alert } = props;
  const { t } = useTranslation();
  const { incidentMapState } = useMapState();
  const { wildfireEvents, floodingEvents, hazards, locations } =
    useAllGeoEvents();
  const history = useHistory();

  const formMethods = useForm<FormValues>({
    defaultValues: {
      isVisible: alert.isVisible,
      geoEvents: alert.geoEvents,
    },
  });
  const { control, handleSubmit, formState } = formMethods;

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<AnyListItemGeoEvent>({
      enabled: Boolean(alert.center),
      filterGeoEvents: (geoEvents) =>
        geoEvents.filter((ge) =>
          isWildfireGeoEvent(ge)
            ? !ge.data.isComplexParent && !ge.data.isPrescribed
            : true,
        ),
      latLng: alert.center!, // only enabled when alert has a center coordinate
      value: alert.geoEvents,
      geoEventTypes: ['wildfire', 'flooding', 'hazard', 'location'],
    });

  const patchAlertMutation = useMutation({
    mutationFn: ({ alertId, data }: { alertId: number; data: SubmitData }) =>
      patchAlert(alertId, data),
  });

  const handleSubmitAlert: SubmitHandler<FormValues> = async (
    values,
  ): Promise<void> => {
    try {
      const submitData: SubmitData = {
        isVisible: values.isVisible,
        geoEventIds: values.geoEvents.map((ge) => ge.id),
      };
      if (
        isEqual(
          {
            isVisible: alert.isVisible,
            geoEventIds: alert.geoEvents.map((ge) => ge.id),
          },
          submitData,
        )
      ) {
        history.push(`/alert/${alert.id}`);
        return;
      }
      await patchAlertMutation.mutateAsync({
        alertId: alert.id,
        data: submitData,
      });
      enqueueSnackbar(t('editAlert.success'), { variant: 'success' });
      history.push(`/alert/${alert.id}`);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : t('common.errorMessage');
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <MapFormLayout>
      <MapFormLayout.MapContent>
        <Map
          center={incidentMapState.center}
          zoom={incidentMapState.zoom}
          mapType={MapType.GenericForm}
          cursor="crosshair"
          searchApi="google"
        >
          <LocationsLayer locations={locations} interactive={false} />
          <HazardsLayer hazards={hazards} interactive={false} />
          <InactiveFloodingsLayer
            floodingEvents={floodingEvents}
            interactive={false}
          />
          <WildfiresLayer
            isFadable
            wildfires={wildfireEvents}
            interactive={false}
          />
          <ActiveFloodingsLayer
            floodingEvents={floodingEvents}
            interactive={false}
          />
          <SingleWarningsAndAlertsLayer alert={alert} />
        </Map>
      </MapFormLayout.MapContent>

      <MapFormLayout.FormContent>
        <FormWithoutEnter onSubmit={handleSubmit(handleSubmitAlert)} noValidate>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <Typography variant="h3">
                {alert.cardDisplayData?.title}
              </Typography>

              <Controller
                name="geoEvents"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <FormControl fullWidth>
                      <AutocompleteWithBackdrop
                        {...geoEventAutocompleteProps}
                        value={field.value}
                        onChange={(_, value) => {
                          field.onChange(value);
                        }}
                        onBlur={field.onBlur}
                        disabled={formState.isSubmitting}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name={field.name}
                            inputRef={field.ref}
                            variant="outlined"
                            label={t('editAlert.inputs.geoEvents.label')}
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </FormControl>
                  );
                }}
              />

              <Controller
                control={control}
                name="isVisible"
                render={({ field, fieldState }) => {
                  const { ref, value, onChange, ...muiFieldProps } = field;
                  return (
                    <TextField
                      select
                      id="field-control-isVisible"
                      label={t('editAlert.inputs.status.label')}
                      fullWidth
                      value={String(value)}
                      onChange={(ev) => onChange(ev.target.value === 'true')}
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                      disabled={formState.isSubmitting}
                    >
                      {getStatusOptions(alert.type === 'wea').map(
                        ({ transKey, value: option }) => {
                          return (
                            <MenuItem key={transKey} value={option}>
                              {t(transKey)}
                            </MenuItem>
                          );
                        },
                      )}
                    </TextField>
                  );
                }}
              />
            </Stack>

            <Button
              type="submit"
              fullWidth
              size="large"
              disabled={formState.isSubmitting || !formState.isDirty}
            >
              {formState.isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                t('editAlert.button')
              )}
            </Button>
          </Stack>
        </FormWithoutEnter>
      </MapFormLayout.FormContent>
    </MapFormLayout>
  );
};
